import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { CheckCircle, Package, Caravan, Shield, Clock, ChevronDown, AlertTriangle, FileText, MapPin } from "lucide-react";
import { SiteLayout } from "@/components/site/SiteLayout";
import { CTASection } from "@/components/site/CTASection";
import utilitaireIcon from "@/assets/icons/utilitaire-red.webp";
import campingcarIcon from "@/assets/icons/campingcar-blue.webp";
import calendrierIcon from "@/assets/icons/calendrier-blue.webp";
import { SEOHead } from "@/components/SEOHead";
import { BreadcrumbSchema, FAQSchema } from "@/components/SchemaOrg";
import { DetailedDefects } from "@/components/site/DetailedDefects";
import ambulancesMercedes from "@/assets/centre/ambulances-mercedes-centre.webp";
import mercedesTaxis from "@/assets/centre/mercedes-taxis.webp";
import bmwTaxi from "@/assets/centre/bmw-taxi-banc.webp";

const vehicleTypes = [
  "Utilitaires légers (jusqu'à 3,5 tonnes)",
  "Fourgons et fourgonnettes",
  "Camionnettes",
  "Pick-up",
  "Véhicules aménagés",
  "Camping-cars",
  "Vans aménagés",
  "Véhicules gros volume",
];

const controlPoints = [
  "Points de contrôle standard (133 points)",
  "Vérification du poids total autorisé",
  "Contrôle des équipements spécifiques",
  "Aménagements intérieurs (camping-cars)",
  "Installation gaz (le cas échéant)",
  "Éclairage et signalisation",
  "Système de freinage adapté",
  "État de la carrosserie et structure",
];

const commonDefectsUtilitaire = [
  { category: "Freinage", details: "Plaquettes très usées (charge lourde), disques voilés, flexible fissuré, frein à main inefficace sous charge" },
  { category: "Pneus", details: "Usure prématurée due au chargement, flancs déformés, pression inadaptée au PTAC" },
  { category: "Éclairage", details: "Feux arrière endommagés (chocs de chargement), phares mal réglés, feux de recul HS" },
  { category: "Suspension", details: "Amortisseurs fatigués par le poids, lames de ressort fissurées, silent-blocs écrasés" },
  { category: "Carrosserie", details: "Corrosion (passages de roue, bas de caisse), portes latérales/arrière endommagées" },
  { category: "Installation gaz", details: "Camping-cars : flexibles périmés, ventilation insuffisante, certificat expiré" },
];

const detailedDefectsUtilitaire = [
  {
    system: "Freinage sous charge",
    percent: 28,
    color: "bg-red-500",
    items: [
      { name: "Plaquettes très usées", description: "charge lourde = usure accélérée" },
      { name: "Disques voilés", description: "freinage vibrant sous charge" },
      { name: "Flexible fissuré", description: "fuite sous pression" },
      { name: "Frein à main inefficace", description: "ne tient pas sous le PTAC" },
    ],
  },
  {
    system: "Pneumatiques",
    percent: 26,
    color: "bg-gray-700",
    items: [
      { name: "Usure prématurée", description: "charge lourde = usure rapide" },
      { name: "Flancs déformés", description: "surcharge fréquente" },
      { name: "Pression inadaptée", description: "non ajustée au PTAC" },
      { name: "Pneus dépareillés", description: "remplacement partiel" },
    ],
  },
  {
    system: "Éclairage & signalisation",
    percent: 24,
    color: "bg-yellow-500",
    items: [
      { name: "Feux arrière endommagés", description: "chocs de chargement" },
      { name: "Phares mal réglés", description: "charge modifie l'assiette" },
      { name: "Feux de recul HS", description: "câblage endommagé" },
      { name: "Éclairage de plaque", description: "souvent cassé" },
    ],
  },
  {
    system: "Suspension & essieux",
    percent: 20,
    color: "bg-blue-600",
    items: [
      { name: "Amortisseurs fatigués", description: "poids constant = usure rapide" },
      { name: "Lames de ressort fissurées", description: "charge excessive" },
      { name: "Silent-blocs écrasés", description: "vibrations transmises" },
    ],
  },
  {
    system: "Structure & carrosserie",
    percent: 15,
    color: "bg-purple-500",
    items: [
      { name: "Corrosion bas de caisse", description: "sel de route et humidité" },
      { name: "Portes arrière endommagées", description: "chocs de chargement" },
      { name: "Passages de roue rouillés", description: "projections et sel" },
    ],
  },
  {
    system: "Installation gaz (camping-cars)",
    percent: 10,
    color: "bg-orange-500",
    items: [
      { name: "Flexibles périmés", description: "date de validité dépassée" },
      { name: "Ventilation insuffisante", description: "normes non respectées" },
      { name: "Certificat gaz expiré", description: "contrôle gaz non renouvelé" },
    ],
  },
];

const UtilitairesPage = () => {
  return (
    <SiteLayout>
      <SEOHead
        title="CT Utilitaire et Camping-car"
        description="Contrôle technique utilitaire et camping-car à Montreuil : fourgons, camionnettes, VUL. Tarifs adaptés, parking adapté. RDV en ligne 24/7."
        path="/vehicules/utilitaires"
      />
      <BreadcrumbSchema items={[{ name: "Accueil", href: "/" }, { name: "Véhicules", href: "/prestations" }, { name: "Utilitaires", href: "/vehicules/utilitaires" }]} />
      <FAQSchema items={[
        { question: "Combien coûte le CT utilitaire ?", answer: "Utilitaire léger : 79€ en ligne (86€ standard). Camping-car : 84€ en ligne (91€ standard)." },
        { question: "Contrôlez-vous les camping-cars ?", answer: "Oui, équipements adaptés. Vérification installations gaz incluse." },
        { question: "Mon utilitaire fait plus de 3,5 tonnes ?", answer: "Nous contrôlons jusqu'à 3,5 tonnes PTAC. Au-delà : centre poids lourds." },
        { question: "Combien de temps dure le CT utilitaire ?", answer: "30-45 minutes. Camping-cars : 45-60 minutes. Résultat immédiat." },
        { question: "Créneaux pour professionnels ?", answer: "Oui, créneaux matinaux et RDV rapides pour professionnels/artisans." },
        { question: "Quels documents apporter ?", answer: "Carte grise. Camping-cars gaz : certificat de contrôle gaz." },
        { question: "Avez-vous du parking adapté ?", answer: "Oui, parking gratuit pouvant accueillir utilitaires et camping-cars." },
        { question: "Le CT est-il différent pour un van aménagé ?", answer: "Le van aménagé suit le même contrôle, avec vérification des aménagements et installations gaz si applicable." },
        { question: "Comment préparer mon utilitaire avant le CT ?", answer: "Déchargez le véhicule, vérifiez l'éclairage, les pneus, les freins et le pare-brise. Pour les camping-cars, assurez-vous que le certificat gaz est à jour." },
      ]} />
      {/* Hero */}
      <section className="relative py-24 bg-gradient-to-br from-autosur-navy to-autosur-blue overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <div 
            className="absolute inset-0 bg-cover bg-center"
            style={{ backgroundImage: `url(${ambulancesMercedes})` }}
          />
        </div>
        <div className="container mx-auto px-6 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-3xl"
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 backdrop-blur-sm rounded-full text-white text-sm font-body mb-6 border border-white/20">
              <img src={utilitaireIcon} alt="Utilitaire" className="w-6 h-6 brightness-0 invert" />
              Utilitaires & Camping-cars
            </div>
            <h1 className="font-heading text-4xl md:text-5xl font-bold text-white mb-6">
              Contrôle Technique Utilitaire & Camping-car à Montreuil-sous-Bois
            </h1>
            <p className="font-body text-white/80 text-lg mb-4">
              Contrôle technique pour vos véhicules utilitaires légers, fourgons, 
              camping-cars et vans aménagés.
            </p>
            <p className="font-body text-white/60 text-sm">
              Inspection VUL • CT fourgon • Visite technique camping-car • Diagnostic véhicule commercial
            </p>
          </motion.div>
        </div>
      </section>

      {/* Info Section */}
      <section className="py-20 bg-background">
        <div className="container mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-12 items-start">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="font-heading text-3xl font-bold text-autosur-navy mb-6">
                Contrôle technique utilitaires et véhicules commerciaux
              </h2>
              <div className="space-y-4 font-body text-muted-foreground">
                <p>
                  Notre centre Autosur Montreuil-sous-Bois est équipé pour contrôler les 
                  <strong className="text-autosur-navy"> véhicules utilitaires légers</strong> (VUL) 
                  jusqu'à 3,5 tonnes de PTAC : fourgons, fourgonnettes, camionnettes, pick-up et véhicules de livraison.
                </p>
                <p>
                  Nous accueillons également les <strong className="text-autosur-navy">camping-cars 
                  et vans aménagés</strong>, avec une attention particulière portée aux équipements 
                  spécifiques : installations gaz, aménagements intérieurs, ventilation et sécurité.
                </p>
                <p>
                  Pour les <strong className="text-autosur-navy">professionnels et artisans</strong>, nous comprenons l'importance de votre véhicule 
                  utilitaire pour votre activité. C'est pourquoi nous proposons des créneaux adaptés pour minimiser l'impact 
                  sur votre journée de travail.
                </p>
              </div>

              {/* Notice */}
              <div className="mt-8 bg-blue-50 rounded-2xl p-6 border border-blue-200">
                <div className="flex items-start gap-4">
                  <Package className="w-6 h-6 text-autosur-blue flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-heading font-bold text-autosur-navy mb-2">
                      Professionnels & Artisans
                    </h3>
                    <p className="font-body text-sm text-autosur-navy">
                      Créneaux matinaux dès 8h30 et rendez-vous rapides 
                      pour les professionnels qui dépendent de leur véhicule utilitaire. 
                      <Link to="/contact" className="text-autosur-blue hover:text-autosur-navy transition-colors"> Réservez en ligne 24/7</Link>.
                    </p>
                  </div>
                </div>
              </div>

              {/* Camping-car notice */}
              <div className="mt-4 bg-green-50 rounded-2xl p-6 border border-green-200">
                <div className="flex items-start gap-4">
                  <Caravan className="w-6 h-6 text-green-600 flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-heading font-bold text-autosur-navy mb-2">
                      Camping-cars & Vans aménagés
                    </h3>
                    <p className="font-body text-sm text-autosur-navy">
                      Pour les camping-cars avec installation gaz, nous vérifions la conformité 
                      de l'installation. Pensez à apporter votre certificat de contrôle gaz à jour.
                    </p>
                  </div>
                </div>
              </div>

              {/* Price box */}
              <div className="mt-6 bg-autosur-blue/5 rounded-2xl p-6 border border-autosur-blue/20">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-heading font-bold text-autosur-navy text-lg">Véhicule utilitaire</p>
                    <p className="font-body text-muted-foreground text-sm">VUL & Camping-car</p>
                  </div>
                  <div className="text-right">
                    <p className="font-heading text-3xl font-bold text-autosur-red">79€</p>
                    <p className="font-body text-muted-foreground text-xs">
                      <span className="line-through">86€</span> À partir de (*)
                    </p>
                  </div>
                </div>
                <Link
                  to="/contact"
                  className="mt-4 block w-full py-3 bg-autosur-red text-white font-heading font-semibold text-center rounded-full hover:bg-autosur-red-light transition-colors"
                >
                  Réserver en ligne – Économisez 7€
                </Link>
                <p className="font-body text-muted-foreground text-[10px] mt-2 text-center">(*) Offre non cumulable</p>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="space-y-6"
            >
              {/* Types de véhicules */}
              <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
                <h3 className="font-heading font-bold text-autosur-navy mb-4">Types d'utilitaires acceptés</h3>
                <ul className="grid grid-cols-1 gap-3">
                  {vehicleTypes.map((type) => (
                    <li key={type} className="flex items-center gap-3">
                      <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                      <span className="font-body text-autosur-navy">{type}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Points de contrôle */}
              <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
                <h3 className="font-heading font-bold text-autosur-navy mb-4">Points de contrôle spécifiques VUL</h3>
                <ul className="grid grid-cols-1 gap-2">
                  {controlPoints.map((point) => (
                    <li key={point} className="flex items-center gap-3">
                      <CheckCircle className="w-5 h-5 text-autosur-blue flex-shrink-0" />
                      <span className="font-body text-autosur-navy text-sm">{point}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Images */}
              <div className="aspect-[4/3] rounded-2xl overflow-hidden shadow-lg">
                <img 
                  src={mercedesTaxis}
                  alt="Mercedes taxis sur banc de contrôle au centre Autosur Montreuil-sous-Bois"
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Défauts courants utilitaires */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h2 className="font-heading text-3xl font-bold text-autosur-navy mb-4">
              Défauts courants détectés sur les utilitaires
            </h2>
            <p className="font-body text-muted-foreground max-w-2xl mx-auto">
              Les véhicules utilitaires sont soumis à des contraintes particulières (charge, kilométrage). 
              Voici les anomalies les plus fréquentes lors de l'inspection VUL.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {commonDefectsUtilitaire.map((defect, index) => (
              <motion.div
                key={defect.category}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100"
              >
                <div className="flex items-center gap-3 mb-3">
                  <AlertTriangle className="w-5 h-5 text-amber-500 flex-shrink-0" />
                  <h3 className="font-heading font-bold text-autosur-navy">{defect.category}</h3>
                </div>
                <p className="font-body text-muted-foreground text-sm">{defect.details}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Detailed Defects Utilitaire */}
      <DetailedDefects
        title="Défauts utilitaires détectés par système"
        subtitle="Les véhicules utilitaires sont soumis à des contraintes spécifiques (charge, kilométrage). Voici le détail des anomalies par système avec leur taux de détection."
        defects={detailedDefectsUtilitaire}
      />

      {/* Préparation utilitaire */}
      <section className="py-20 bg-background">
        <div className="container mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-12 items-center max-w-5xl mx-auto">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="font-heading text-3xl font-bold text-autosur-navy mb-4">
                Préparer votre utilitaire avant le contrôle
              </h2>
              <p className="font-body text-muted-foreground mb-6">
                Une préparation adaptée aux spécificités de votre <strong className="text-autosur-navy">véhicule commercial</strong> vous 
                évite la contre-visite et les interruptions d'activité.
              </p>
              <ul className="space-y-3">
                {[
                  "Décharger le véhicule au maximum pour faciliter l'inspection",
                  "Vérifier l'ensemble de l'éclairage (feux arrière souvent endommagés par les chargements)",
                  "Contrôler l'état des pneus et adapter la pression au PTAC",
                  "Vérifier les portes latérales et arrière (fermeture, charnières)",
                  "S'assurer du bon fonctionnement du frein à main sous charge",
                  "Pour les camping-cars : mettre à jour le certificat gaz",
                  "Nettoyer le pare-brise (fissures dans le champ de vision = refus)",
                  "Apporter la carte grise et le certificat gaz si applicable",
                ].map((item) => (
                  <li key={item} className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                    <span className="font-body text-autosur-navy text-sm">{item}</span>
                  </li>
                ))}
              </ul>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="space-y-4"
            >
              <div className="aspect-[4/3] rounded-2xl overflow-hidden shadow-lg">
                <img 
                  src={ambulancesMercedes}
                  alt="Ambulances Mercedes au centre de contrôle technique Autosur"
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
              </div>
              <div className="aspect-[4/3] rounded-2xl overflow-hidden shadow-lg">
                <img 
                  src={bmwTaxi}
                  alt="BMW taxi sur banc de contrôle Autosur Montreuil"
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Advantages */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h2 className="font-heading text-3xl font-bold text-autosur-navy mb-4">
              Pourquoi choisir Autosur pour votre CT utilitaire ?
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-4 gap-6">
            {[
              { iconSrc: calendrierIcon, title: "RDV en ligne", desc: "Réservez 24h/24" },
              { icon: Shield, title: "Centre agréé", desc: "Homologation État" },
              { icon: Clock, title: "Rapidité", desc: "30-60 minutes" },
              { icon: CheckCircle, title: "Résultat immédiat", desc: "PV instantané" },
            ].map((item, index) => (
              <motion.div
                key={item.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-white rounded-2xl p-6 text-center shadow-sm border border-gray-100 hover:shadow-md hover:-translate-y-1 transition-all duration-300 cursor-default"
              >
                <div className="w-12 h-12 mx-auto mb-4 bg-autosur-blue/10 rounded-xl flex items-center justify-center">
                  {item.iconSrc ? (
                    <img src={item.iconSrc} alt={item.title} className="w-7 h-7 object-contain" />
                  ) : item.icon ? (
                    <item.icon className="w-6 h-6 text-autosur-blue" />
                  ) : null}
                </div>
                <h3 className="font-heading font-bold text-autosur-navy mb-2">{item.title}</h3>
                <p className="font-body text-muted-foreground text-sm">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Internal links */}
      <section className="py-16 bg-background">
        <div className="container mx-auto px-6">
          <h2 className="font-heading text-2xl font-bold text-autosur-navy mb-6 text-center">
            Découvrez aussi nos autres contrôles techniques
          </h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 max-w-4xl mx-auto mb-8">
            <Link to="/vehicules/voitures" className="bg-white rounded-xl p-5 border border-gray-100 shadow-sm hover:shadow-md hover:border-autosur-blue/30 transition-all group">
              <h3 className="font-heading font-bold text-autosur-navy text-sm mb-1 group-hover:text-autosur-blue transition-colors">CT voiture</h3>
              <p className="font-body text-muted-foreground text-xs">Berlines, SUV, citadines • Dès 73€</p>
            </Link>
            <Link to="/vehicules/motos" className="bg-white rounded-xl p-5 border border-gray-100 shadow-sm hover:shadow-md hover:border-autosur-blue/30 transition-all group">
              <h3 className="font-heading font-bold text-autosur-navy text-sm mb-1 group-hover:text-autosur-blue transition-colors">CT moto</h3>
              <p className="font-body text-muted-foreground text-xs">Obligatoire depuis 2024 • Dès 70€</p>
            </Link>
            <Link to="/vehicules/collection" className="bg-white rounded-xl p-5 border border-gray-100 shadow-sm hover:shadow-md hover:border-autosur-blue/30 transition-all group">
              <h3 className="font-heading font-bold text-autosur-navy text-sm mb-1 group-hover:text-autosur-blue transition-colors">CT collection</h3>
              <p className="font-body text-muted-foreground text-xs">Véhicules anciens • Dès 73€</p>
            </Link>
            <Link to="/prestations" className="bg-white rounded-xl p-5 border border-gray-100 shadow-sm hover:shadow-md hover:border-autosur-blue/30 transition-all group">
              <h3 className="font-heading font-bold text-autosur-navy text-sm mb-1 group-hover:text-autosur-blue transition-colors">Tous nos tarifs</h3>
              <p className="font-body text-muted-foreground text-xs">Grille tarifaire complète</p>
            </Link>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 max-w-3xl mx-auto">
            <Link to="/blog/economiser-controle-technique-93-astuces" className="flex items-center gap-2 px-4 py-3 bg-white border border-gray-100 rounded-xl font-body text-autosur-navy text-sm hover:border-autosur-blue/30 hover:bg-autosur-blue/5 transition-all">
              <FileText className="w-4 h-4 text-autosur-blue flex-shrink-0" />
              Économiser sur son CT
            </Link>
            <Link to="/controle-technique-pas-cher-93" className="flex items-center gap-2 px-4 py-3 bg-white border border-gray-100 rounded-xl font-body text-autosur-navy text-sm hover:border-autosur-blue/30 hover:bg-autosur-blue/5 transition-all">
              <FileText className="w-4 h-4 text-autosur-blue flex-shrink-0" />
              CT pas cher en 93
            </Link>
            <Link to="/faq" className="flex items-center gap-2 px-4 py-3 bg-white border border-gray-100 rounded-xl font-body text-autosur-navy text-sm hover:border-autosur-blue/30 hover:bg-autosur-blue/5 transition-all">
              <FileText className="w-4 h-4 text-autosur-blue flex-shrink-0" />
              Toutes nos FAQ
            </Link>
          </div>

          <div className="mt-10 text-center">
            <h3 className="font-heading font-bold text-autosur-navy mb-4">CT utilitaire près de chez vous</h3>
            <div className="flex flex-wrap gap-2 justify-center">
              {["controle-technique-bagnolet", "controle-technique-vincennes", "controle-technique-romainville", "controle-technique-noisy-le-sec", "controle-technique-bondy", "controle-technique-rosny-sous-bois"].map((slug) => (
                <Link
                  key={slug}
                  to={`/${slug}`}
                  className="inline-flex items-center gap-1 px-4 py-2 bg-white border border-gray-200 rounded-full font-body text-autosur-navy text-xs hover:border-autosur-blue/50 hover:bg-autosur-blue/5 transition-all"
                >
                  <MapPin className="w-3 h-3 text-autosur-blue" />
                  {slug.replace("controle-technique-", "").replace(/-/g, " ").replace(/\b\w/g, l => l.toUpperCase())}
                </Link>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="max-w-3xl mx-auto"
          >
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-autosur-navy mb-8 text-center">
              Questions fréquentes sur le CT utilitaire
            </h2>
            <div className="space-y-4">
              {[
                { q: "Combien coûte le CT utilitaire ?", a: "Le contrôle technique utilitaire léger est à partir de 79€ en ligne (86€ standard). Camping-car et gros volume : à partir de 84€ en ligne (91€ standard). Économisez 7€ en réservant en ligne sur notre site." },
                { q: "Contrôlez-vous les camping-cars et vans aménagés ?", a: "Oui ! Notre centre dispose des équipements nécessaires pour contrôler camping-cars, vans aménagés et véhicules gros volume. Nous vérifions également la conformité des installations gaz et les aménagements intérieurs." },
                { q: "Mon utilitaire fait plus de 3,5 tonnes, est-il accepté ?", a: "Notre centre contrôle les véhicules utilitaires légers (VUL) jusqu'à 3,5 tonnes de PTAC. Au-delà, vous devez vous adresser à un centre agréé poids lourds. Vérifiez le PTAC sur votre carte grise (rubrique F2)." },
                { q: "Combien de temps dure le CT utilitaire ?", a: "Un contrôle technique utilitaire prend environ 30 à 45 minutes. Pour les camping-cars et gros volumes avec vérification des installations spécifiques, comptez 45 à 60 minutes. Le résultat est immédiat." },
                { q: "Proposez-vous des créneaux adaptés aux professionnels ?", a: "Oui ! Nous proposons des créneaux dès 8h30 le matin et des rendez-vous rapides pour les professionnels, artisans et livreurs qui dépendent de leur véhicule commercial. Réservez en ligne 24/7 pour garantir votre créneau." },
                { q: "Quels documents apporter pour le CT utilitaire ?", a: "Apportez la carte grise (certificat d'immatriculation) de votre véhicule. Pour une contre-visite, apportez l'ancien procès-verbal. Pour les camping-cars avec installation gaz, le certificat de contrôle gaz à jour est nécessaire." },
                { q: "Avez-vous du parking adapté aux utilitaires ?", a: "Oui, notre parking gratuit et sécurisé peut accueillir les véhicules utilitaires, fourgons et camping-cars. Accès facile depuis le Boulevard Aristide Briand, sans difficulté de manœuvre." },
                { q: "Le CT est-il différent pour un van aménagé ?", a: "Le van aménagé (type Volkswagen California, Mercedes Marco Polo, etc.) suit le même contrôle que les utilitaires, avec une vérification supplémentaire des aménagements et installations gaz si applicable." },
                { q: "Comment préparer mon utilitaire avant la visite technique ?", a: "Déchargez le véhicule, vérifiez tous les éclairages (surtout les feux arrière), contrôlez les pneus, vérifiez le pare-brise et assurez-vous que le frein à main fonctionne correctement. Pour les camping-cars, mettez à jour le certificat de contrôle gaz." },
              ].map((faq, index) => (
                <details key={index} className="bg-white rounded-xl border border-gray-100 shadow-sm group">
                  <summary className="flex items-center justify-between p-5 cursor-pointer font-heading font-semibold text-autosur-navy text-sm hover:text-autosur-blue transition-colors">
                    {faq.q}
                    <ChevronDown className="w-5 h-5 text-muted-foreground group-open:rotate-180 transition-transform flex-shrink-0 ml-4" />
                  </summary>
                  <div className="px-5 pb-5 pt-0">
                    <p className="font-body text-muted-foreground text-sm">{faq.a}</p>
                  </div>
                </details>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      <CTASection 
        title="Un utilitaire ou camping-car à contrôler ?"
        subtitle="Profitez de notre expertise pour tous vos véhicules professionnels et de loisirs."
      />
    </SiteLayout>
  );
};

export default UtilitairesPage;
